import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Typography
} from 'antd'
import * as dayjs from 'dayjs'
import { NumericFormat, PatternFormat } from 'react-number-format'
import axios from 'axios'

import { generateBuildingEstimateXLSX } from '../../../components/BuildingEstimateExport'

import advantageBuildingOptions from '../../../data/advantage/options'
import advantageBuildingAmounts from '../../../data/advantage/amounts'
import advantageBuildingAddons from '../../../data/advantage/addons'

import integrityBuildingOptions from '../../../data/integrity/options'
import integrityBuildingAmounts from '../../../data/integrity/amounts'
import integrityBuildingAddons from '../../../data/integrity/addons'

import integrityCattleBuildingOptions from '../../../data/integrity/cattle/options'
import integrityCattleBuildingAmounts from '../../../data/integrity/cattle/amounts'
import integrityCattleBuildingAddons from '../../../data/integrity/cattle/addons'

import { convertCamelCaseToTitleCase, convertDashCaseToTitleCase, convertStringToTitleCase, generateTableColumns } from '../../../helpers'

import { userContext } from '../../../context/User.js'

const { Title } = Typography

const ViewBuildingEstimate = forwardRef((props, ref) => {
  const [isPartsTableExpanded, setPartsTableExpanded] = useState();
  const [pdfUrls, setPdfUrls] = useState({})
  const data = props.data
  const totals = data.totals
  const [ estimateOptions, setEstimateOptions ] = useState([])
  const user = useContext(userContext)
  const isAdmin = user.permissions.includes('manage-estimates')

  const fetchPdfUrls = async () => {
    if (!isAdmin) return

    try {
      const buildingType = data.inputData?.buildingType
      if (!buildingType) return

      const [partsResponse, materialsResponse] = await Promise.all([
        axios.get('/api/parts-materials/urls', {
          params: { buildingType, dataType: 'parts' },
          withCredentials: true
        }).catch(() => ({ data: [] })),
        axios.get('/api/parts-materials/urls', {
          params: { buildingType, dataType: 'materials' },
          withCredentials: true
        }).catch(() => ({ data: [] }))
      ])

      const urlMap = {}
      partsResponse.data.forEach(item => {
        urlMap[item.partNumber] = item.pdfUrl
      })
      materialsResponse.data.forEach(item => {
        urlMap[item.partNumber] = item.pdfUrl
      })

      setPdfUrls(urlMap)
    } catch (error) {
      console.error('Error fetching PDF URLs:', error)
    }
  }

  const getBuildingTypeData = (buildingType) => {
    switch(buildingType) {
      case 'integrity':
        return [ ...integrityBuildingOptions, ...integrityBuildingAmounts, ...integrityBuildingAddons ]
      case 'integrity-cattle':
        return [ ...integrityCattleBuildingOptions, ...integrityCattleBuildingAmounts, ...integrityCattleBuildingAddons ]
      case 'advantage':
      default:
        return [ ...advantageBuildingOptions, ...advantageBuildingAmounts, ...advantageBuildingAddons ]
    }
  }

  const populateEstimateOptions = (inputData) => {
    const optionData = getBuildingTypeData(inputData.buildingType)
  
    const options = optionData
      .filter(keyData => keyData.name !== 'discountPercentage' && keyData.name !== 'notes' && keyData.slug !== 'discountPercentage' && keyData.slug !== 'notes' && keyData.name !== 'buildingWidth')
      .map(keyData => {
        const key = keyData.name
        const slug = keyData.slug
        let option = keyData.label ?? keyData.description ?? keyData.name ?? key
        let value = 'No'
        
        const inputKey = inputData.hasOwnProperty(key) ? key : (inputData.hasOwnProperty(slug) ? slug : null)
        
        if (inputKey) {
          if (inputData[inputKey] === true) {
            value = 'Yes'
          } else if (inputData[inputKey] === false || inputData[inputKey] === null || inputData[inputKey] === undefined || inputData[inputKey] === '' || inputData[inputKey] === 'N/A') {
            value = 'No'
          } else if (typeof inputData[inputKey] === 'string') {
            value = convertStringToTitleCase(convertDashCaseToTitleCase(inputData[inputKey]))
          } else if (inputData[inputKey] !== null && inputData[inputKey] !== undefined && inputData[inputKey] !== '') {
            value = inputData[inputKey]
          } else {
            value = 'No'
          }

          // Add units for specific amount fields
          if (inputKey === 'windload' && typeof value === 'number' && value > 0) {
            value = value + ' MPH'
          } else if (inputKey === 'groundSnowLoad' && typeof value === 'number' && value > 0) {
            value = value + ' PSF'
          }
  
          if (keyData.options) {
            const valueData = keyData.options.find(item => item.value === inputData[inputKey])
            if (valueData && valueData.label) {
              value = valueData.label
            }
          }
        }
  
        return { key: inputKey || key, option, value }
      })

    // Add buildingLength row (added 3rd, so it appears 3rd)
    if (inputData.buildingLength) {
      options.unshift({ key: 'buildingLength', option: 'Building Length', value: inputData.buildingLength + ' ft' })
    }

    // Add buildingWidth row (added 2nd, so it appears 2nd)
    if (inputData.buildingWidth) {
      // Find the building width option to get the display label
      const buildingData = [...advantageBuildingOptions, ...integrityBuildingOptions, ...integrityCattleBuildingOptions]
        .find(item => item.name === 'buildingWidth')
      let buildingWidthValue = inputData.buildingWidth
      if (buildingData && buildingData.options) {
        const valueData = buildingData.options.find(item => item.value === inputData.buildingWidth)
        if (valueData && valueData.label) {
          buildingWidthValue = valueData.label
        }
      }
      options.unshift({ key: 'buildingWidth', option: 'Building Width', value: buildingWidthValue })
    }

    // Add buildingType row (added 1st, so it appears 1st)
    if (inputData.buildingType) {
      let buildingTypeValue = convertStringToTitleCase(convertDashCaseToTitleCase(inputData.buildingType))
      options.unshift({ key: 'buildingType', option: 'Building Type', value: buildingTypeValue })
    }

  
    setEstimateOptions(options)
  }

  useImperativeHandle(ref, () => ({
    onDownload() {
      generateBuildingEstimateXLSX(data, estimateOptions)
    }
  }))

  useEffect(() => {
    populateEstimateOptions(data.inputData)
    fetchPdfUrls()
  }, [])

  return ( (!data) ? <p>Could not find estimate</p> :
    <>
      <Title level={2}>{ data.title }</Title>

      <b>Created Date</b>: { dayjs(data.createdDate).format('M/D/YY h:mma') }<br />
      <b>Last Modified Date</b>: { dayjs(data.modifiedDate).format('M/D/YY h:mma') }<br />
      { data.requestedPickUpDate && <><b>Requested Pick-Up Date</b>: { dayjs(data.requestedPickUpDate).format('M/D/YY') }<br /></> }
      <b>Creator</b>: { data.creator ? (data.creator.first_name + ' ' + data.creator.last_name) : '(User Not Found)' }<br />
      <b>Dealer</b>: { data.dealer ? (data.dealer.first_name + ' ' + data.dealer.last_name) : '(User Not Found)' }<br />
      <b>Customer</b>: { data.customerName ?? <i>(None)</i> }<br />
      <b>Phone Number</b>: { data.inputData?.phoneNumber ? <PatternFormat value={data.inputData.phoneNumber} displayType={'text'} format="(###) ###-####" /> : <i>(None)</i> }<br />
      <b>Customer Address</b>: { data.customerAddress ?? <i>(None)</i> }<br />
      <b>Company Name</b>: { data.inputData?.companyName ?? <i>(None)</i> }<br />
      <b>Building City</b>: { data.inputData?.buildingCity ?? <i>(None)</i> }<br />
      <b>Building State</b>: { data.inputData?.buildingState ?? <i>(None)</i> }<br />
      <b>Building Zip</b>: { data.inputData?.buildingZip ?? <i>(None)</i> }<br />
      <b>Status</b>: { convertStringToTitleCase(data.status) }<br />

      <br /><br />
      <div id="view-estimate-options" style={{ width: '50%' }}>
        <h3>Options</h3>
        <Table
          columns={ generateTableColumns(['option', 'value']) }
          rowKey="key"
          dataSource={ estimateOptions }
          pagination={ false }
          size="small"
          showHeader={ false }
        />
      </div>

      {data.inputData?.notes && (
        <div id="view-estimate-notes" style={{ width: '50%' }}>
          <br /><br />
          <h3>Notes</h3>
          <Card style={{ border: '1px solid gray' }}>
            <p style={{ whiteSpace: 'pre-line' }}>{data.inputData.notes}</p>
          </Card>
        </div>
      )}
      
      <br /><br />
      <div id="view-estimate-parts">
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px',
          marginBottom: '16px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
        }}>
          <div 
            onClick={() => setPartsTableExpanded(!isPartsTableExpanded)} 
            style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 24px',
              backgroundColor: 'white',
              cursor: 'pointer',
              borderBottom: '1px solid #e5e7eb'
            }}
          >
            <span style={{ 
              fontWeight: 500,
              color: '#1f2937'
            }}>Parts / Part Numbers</span>
            <Button 
              type="primary"
              size="medium"
              style={{
                backgroundColor: '#8B1F41',
                borderColor: '#8B1F41',
                pointerEvents: 'none'
              }}
            >
              {isPartsTableExpanded ? 'Close' : 'Open'}
            </Button>
          </div>
          
          {isPartsTableExpanded && (
            <div style={{ padding: '16px' }}>
              <Table
                columns={[
                  {
                    title: 'Part Number',
                    dataIndex: 'partNumber',
                    key: 'partNumber',
                    render: (value) => value || 'N/A'
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description',
                    render: (value, record) => {
                      if (isAdmin && pdfUrls[record.partNumber]) {
                        return (
                          <a
                            href={pdfUrls[record.partNumber].startsWith('http') ? pdfUrls[record.partNumber] : `https://${pdfUrls[record.partNumber]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1890ff', textDecoration: 'underline' }}
                          >
                            {value}
                          </a>
                        )
                      }
                      return value
                    }
                  },
                  {
                    title: 'Required Qty',
                    dataIndex: 'requiredQty',
                    key: 'requiredQty',
                    render: (value) => <NumericFormat value={value} displayType={'text'} thousandSeparator={true} />
                  },
                  {
                    title: 'Add Qty',
                    dataIndex: 'addQty',
                    key: 'addQty',
                    render: (value) => value || 0
                  }
                ]}
                rowKey="id"
                dataSource={
                  data.parts.map(part => {
                    if (part.alternate && data && data.inputData) {
                      const fieldValue = data.inputData[part.alternate.field]

                      if (fieldValue === part.alternate.value) {
                        if (part.alternate.properties.description) {
                          part.description = part.alternate.properties.description
                        }

                        if (part.alternate.properties.partNumber) {
                          part.partNumber = part.alternate.properties.partNumber
                        }
                      }
                    }

                    return part
                  })
                }
                pagination={false}
                size="small"
              />
            </div>
          )}
        </div>
      </div>

      <br /><br />
      <Card id="view-estimate-totals" title="Totals" style={{ display: 'inline-block' }}>
        <p><b>Sqft</b>: { <NumericFormat value={totals.sqft} displayType={'text'} thousandSeparator={true} decimalScale={0} /> }</p>
        <p><b>Shipping Weight</b>: { <NumericFormat value={totals.weight} displayType={'text'} thousandSeparator={true} /> }</p>
        <p><b>Suggested Retail Price</b>: { <NumericFormat value={totals.suggestedPrice} displayType={'text'} thousandSeparator={true} prefix={'$'} /> }</p>
        { isAdmin && <p><b>Dealer Discount</b>: { <NumericFormat value={totals.discountPercentage} displayType={'text'} suffix={'%'} /> }</p> }
        <p><b>Dealer Price</b>: { <NumericFormat value={totals.amountDue} displayType={'text'} thousandSeparator={true} prefix={'$'} /> }</p>
        <p><b>Price Per Sqft</b>: { <NumericFormat value={totals.pricePerSqFt} displayType={'text'} thousandSeparator={true} prefix={'$'} /> }</p>
        <p><b>Deposit Required</b>: { <NumericFormat value={totals.deposit} displayType={'text'} thousandSeparator={true} prefix={'$'} /> }</p>
        <p><b>Amount Due at Pickup</b>: { <NumericFormat value={totals.amountDueAtPickup} displayType={'text'} thousandSeparator={true} prefix={'$'} /> }</p>
      </Card>
    </>
  )
})

export default ViewBuildingEstimate